import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {getComments, getInitialComment, getLatestComment, getLatestReply} from './thread-comment-accessors'

export const ThreadGroupVariant = {
  Commented: 'commented',
  Replied: 'replied',
  Mentioned: 'mentioned',
  RepliedMentioned: 'repliedMentioned',
  Resolved: 'resolved',
} as const

export type ThreadGroupVariant = (typeof ThreadGroupVariant)[keyof typeof ThreadGroupVariant]
export type UnresolvedThreadGroupVariant = Exclude<ThreadGroupVariant, typeof ThreadGroupVariant.Resolved>

export const ThreadGroupVariantLabels: Record<ThreadGroupVariant, string> = {
  [ThreadGroupVariant.Commented]: 'commented',
  [ThreadGroupVariant.Replied]: 'replied to your comment',
  [ThreadGroupVariant.Mentioned]: 'mentioned you',
  [ThreadGroupVariant.RepliedMentioned]: "replied to a comment you're mentioned in",
  [ThreadGroupVariant.Resolved]: 'commented',
}

/**
 * Returns true when a comment's body `@`-mentions the given login.
 * Matches the raw body text on a username token boundary (case-insensitive), so
 * `@octocat` matches but `@octocat-bot` or an email-style `foo@octocat` does not.
 */
export function bodyMentionsViewer(comment: Comment | undefined, viewerLogin: string | undefined): boolean {
  if (!comment?.body || !viewerLogin) return false

  const escapedLogin = viewerLogin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const mentionPattern = new RegExp(`(^|[^a-z0-9-])@${escapedLogin}(?![a-z0-9-])`, 'i')
  return mentionPattern.test(comment.body)
}

/**
 * Classifies a thread into exactly one {@link UnresolvedThreadGroupVariant}.
 *
 * Unresolved threads are evaluated in precedence order (first match wins):
 *  1. `mentioned` — the latest comment mentions the viewer
 *  2. `replied` — the viewer authored the initial comment and someone replied
 *  3. `repliedMentioned` — the viewer is mentioned in an earlier comment with a newer reply
 *  4. `commented` — everything else
 *
 * `viewerLogin` is `undefined` when logged out, in which case authorship and mention
 * checks are skipped and unresolved threads fall through to `commented`.
 */
export function classifyUnresolvedThread(
  thread: Thread,
  viewerLogin: string | undefined,
): UnresolvedThreadGroupVariant {
  const comments = getComments(thread)
  const initial = comments[0]
  const latest = comments[comments.length - 1]

  if (!initial || !latest) return ThreadGroupVariant.Commented

  const hasReply = comments.length > 1
  const viewerAuthoredLatest = latest.viewerDidAuthor ?? false

  // 1. The freshest activity is a comment that mentions the viewer.
  if (!viewerAuthoredLatest && bodyMentionsViewer(latest, viewerLogin)) {
    return ThreadGroupVariant.Mentioned
  }

  // 2. Someone replied to a comment the viewer started.
  if ((initial.viewerDidAuthor ?? false) && hasReply && !viewerAuthoredLatest) {
    return ThreadGroupVariant.Replied
  }

  // 3. A newer reply landed on a thread where the viewer was mentioned earlier.
  if (hasReply && !viewerAuthoredLatest) {
    const earlierComments = comments.slice(0, -1)
    if (earlierComments.some(comment => bodyMentionsViewer(comment, viewerLogin))) {
      return ThreadGroupVariant.RepliedMentioned
    }
  }

  // 4. Catch-all.
  return ThreadGroupVariant.Commented
}

/**
 * Extracts the single comment whose author becomes the headline's "X" for the given variant.
 */
export function getVariantComment(thread: Thread, variant: ThreadGroupVariant): Comment | undefined {
  switch (variant) {
    case ThreadGroupVariant.Replied:
    case ThreadGroupVariant.RepliedMentioned:
      return getLatestReply(thread) ?? getLatestComment(thread)
    case ThreadGroupVariant.Mentioned:
      // "X" is whoever wrote the comment that mentioned the viewer.
      // TODO: detect the mentioning comment explicitly; fall back to the latest comment for now.
      return getLatestComment(thread)
    case ThreadGroupVariant.Resolved:
    case ThreadGroupVariant.Commented:
    default:
      return getInitialComment(thread)
  }
}
